import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Clock, Phone, ChevronRight, CalendarCheck, Menu, X, Calendar, Lock, MessageCircle, Scissors, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getUnits, getPlans, getClientSubscription } from '@/services/api';
import { AuthModal } from '@/components/features/auth';
import { toast } from 'sonner';


const HomePage = () => {
  const [units, setUnits] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Estados de autenticação e assinatura (carregados do localStorage)
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userSubscription, setUserSubscription] = useState(null);
  const [clientName, setClientName] = useState('');

  // Estado para modal de autenticação
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedPlanForAuth, setSelectedPlanForAuth] = useState(null);

  // Estado para unidade selecionada na seção de planos
  const [selectedPlanUnitId, setSelectedPlanUnitId] = useState(null);

  // Estado para popup de WhatsApp
  const [showWhatsAppPopup, setShowWhatsAppPopup] = useState(false);

  const whatsappUnits = [
    { label: 'Brandão Matriz', phone: '5579999353072' },
    { label: 'Brandão Unidade 2', phone: '5579998487035' },
  ];

  // Verificar autenticação ao montar o componente
  useEffect(() => {
    const checkAuth = () => {
      const isLogged = localStorage.getItem('isClientLoggedIn') === 'true';
      const storedSubscription = localStorage.getItem('clientSubscription');
      const storedName = localStorage.getItem('clientName');

      setIsLoggedIn(isLogged);
      if (isLogged && storedSubscription) {
        setUserSubscription(JSON.parse(storedSubscription));
      }
      if (storedName) {
        setClientName(storedName);
      }
    };

    checkAuth();

    // Re-validar assinatura com a API para garantir dados atualizados
    const storedPhone = localStorage.getItem('clientPhone');
    const isLogged = localStorage.getItem('isClientLoggedIn') === 'true';
    if (isLogged && storedPhone) {
      getClientSubscription(storedPhone)
        .then(res => {
          if (res?.data) {
            const sub = res.data;
            const normalized = {
              planId: sub.plan_id,
              planName: sub.plan?.name || 'Plano',
              planDescription: sub.plan?.description || '',
              cutsLimit: sub.plan?.cuts_limit || 0,
              cutsUsed: sub.cuts_used || 0,
              expiryDate: sub.expiry_date,
              status: sub.status,
              unitId: sub.unit_id || null,
              planServiceIds: sub.plan?.service_ids || [],
              planServicesDetail: sub.plan?.plan_services_detail || [],
              serviceUsage: sub.service_usage || {},
            };
            setUserSubscription(normalized);
            localStorage.setItem('clientSubscription', JSON.stringify(normalized));
          }
        })
        .catch(() => {
          setUserSubscription(null);
          localStorage.removeItem('clientSubscription');
        });
    }

    // Listener para mudanças no storage (útil para múltiplas abas)
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);


  const handleLogout = () => {
    localStorage.removeItem('isClientLoggedIn');
    localStorage.removeItem('clientToken');
    localStorage.removeItem('clientId');
    localStorage.removeItem('clientEmail');
    localStorage.removeItem('clientName');
    localStorage.removeItem('clientPhone');
    localStorage.removeItem('clientCpf');
    localStorage.removeItem('clientSubscription');
    localStorage.removeItem('savedClientName');
    localStorage.removeItem('savedClientPhone');
    localStorage.removeItem('savedClientEmail');
    localStorage.removeItem('pendingPlanSubscription');
    setIsLoggedIn(false);
    setUserSubscription(null);
    setClientName('');
    toast.success('Logout realizado com sucesso!');
  };

  // Handler para assinar plano (mostra modal se não logado)
  const handleSubscribePlan = (plan) => {
    // Obriga seleção de unidade antes de prosseguir
    if (!plan.selectedUnitId) {
      toast.error('Selecione uma unidade acima para continuar', { duration: 3000 });
      document.getElementById('unit-selector')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (isLoggedIn) {
      // Se já está logado, vai direto para o pagamento
      localStorage.setItem('pendingPlanSubscription', JSON.stringify(plan));
      navigate(`/pagamento-plano?plano=${plan.id}`);
    } else {
      // Se não está logado, mostra o modal de autenticação
      setSelectedPlanForAuth(plan);
      setShowAuthModal(true);
    }
  };

  // Handler para renovar plano atual
  const handleRenewPlan = (planId, planWithUnit) => {
    const plan = planWithUnit || plans.find(p => String(p.id) === String(planId));
    if (plan) localStorage.setItem('pendingPlanSubscription', JSON.stringify(plan));
    navigate(`/pagamento-plano?plano=${planId}`);
  };

  // Handler para trocar de plano
  const handleChangePlan = (planId, planWithUnit) => {
    const plan = planWithUnit || plans.find(p => String(p.id) === String(planId));
    if (plan) localStorage.setItem('pendingPlanSubscription', JSON.stringify(plan));
    navigate(`/pagamento-plano?plano=${planId}`);
  };



  useEffect(() => {
    const initializeData = async () => {
      try {
        const [unitsRes, plansRes] = await Promise.all([getUnits(), getPlans()]);
        setUnits(unitsRes.data || []);
        setPlans(plansRes.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  const handleSelectUnit = (address) => {
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    window.open(mapsUrl, '_blank');
  };

  const WhatsAppIcon = ({ className }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );

  const menuItems = [
    {
      icon: WhatsAppIcon,
      label: 'Contato',
      onClick: () => {
        setMenuOpen(false);
        setTimeout(() => {
          document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 350);
      }
    },
    ...(!isLoggedIn ? [
      {
        icon: Lock,
        label: 'Login',
        onClick: () => {
          setMenuOpen(false);
          navigate('/login');
        }
      }
    ] : [
      {
        icon: CreditCard,
        label: 'Minha Assinatura',
        onClick: () => {
          setMenuOpen(false);
          navigate('/minha-assinatura');
        }
      },
      {
        icon: Lock,
        label: 'Sair',
        onClick: () => {
          setMenuOpen(false);
          handleLogout();
        }
      }
    ])
  ];

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Popup de seleção de unidade WhatsApp - nível raiz para evitar quebra de fixed pelo transform */}
      <AnimatePresence>
        {showWhatsAppPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
            onClick={() => setShowWhatsAppPopup(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 16 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="w-full max-w-sm bg-[#111111] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5">
                <p className="font-oswald text-xs uppercase tracking-widest text-[#C6A87C] mb-1">Fale conosco</p>
                <h3 className="font-oswald font-bold text-lg uppercase text-white mb-4">Escolha a unidade</h3>
                <div className="space-y-2">
                  {whatsappUnits.map((unit) => (
                    <a
                      key={unit.phone}
                      href={`https://wa.me/${unit.phone}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setShowWhatsAppPopup(false)}
                      className="flex items-center gap-3 px-4 py-3.5 bg-green-600/10 hover:bg-green-600/20 border border-green-600/20 hover:border-green-500/50 rounded-xl transition-all group"
                    >
                      <div className="w-8 h-8 bg-green-600/20 rounded-lg flex items-center justify-center shrink-0">
                        <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                      </div>
                      <span className="font-oswald text-sm uppercase tracking-widest text-white group-hover:text-green-400 transition-colors flex-1">{unit.label}</span>
                      <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-green-400 group-hover:translate-x-0.5 transition-all" />
                    </a>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setShowWhatsAppPopup(false)}
                  className="mt-4 w-full py-2.5 rounded-xl border border-white/10 text-slate-500 hover:text-white hover:border-white/20 font-oswald text-xs uppercase tracking-widest transition-all"
                >
                  Cancelar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Menu Hamburguer Button - Fixed */}
      <AnimatePresence>
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.3 }}
          onClick={() => setMenuOpen(!menuOpen)}
          className="fixed top-0 left-6 z-[60] w-12 h-12 bg-[#C6A87C] hover:bg-[#B8986D] rounded-full flex items-center justify-center transition-colors shadow-lg mt-6"
          whileTap={{ scale: 0.95 }}
        >
          <AnimatePresence mode="wait">
            {menuOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <X className="w-6 h-6 text-black" strokeWidth={2.5} />
              </motion.div>
            ) : (
              <motion.div
                key="menu"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Menu className="w-6 h-6 text-black" strokeWidth={2.5} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
      </AnimatePresence>

      {/* ── MENU DESKTOP: popup flutuante posicionado sob o botão ── */}
      <AnimatePresence>
        {menuOpen && (
          <>
            {/* Backdrop invisivel para fechar ao clicar fora — apenas desktop */}
            <div
              className="hidden md:block fixed inset-0 z-40"
              onClick={() => setMenuOpen(false)}
            />

            {/* Popup flutuante — desktop */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: -8 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="hidden md:block fixed top-20 left-6 z-50 w-72 bg-[#111111] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="p-2 space-y-1">
                {menuItems.map((item, index) => (
                  <motion.button
                    key={item.label}
                    initial={{ x: -12, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.06 }}
                    onClick={item.onClick}
                    className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-white/5 border border-transparent hover:border-[#C6A87C]/20 transition-all group rounded-xl"
                  >
                    <div className="w-8 h-8 bg-[#C6A87C]/10 rounded-lg flex items-center justify-center group-hover:bg-[#C6A87C]/20 transition-colors shrink-0">
                      <item.icon className="w-4 h-4 text-[#C6A87C]" strokeWidth={2} />
                    </div>
                    <span className="font-oswald text-sm uppercase tracking-widest text-white group-hover:text-[#C6A87C] transition-colors flex-1 text-left">
                      {item.label}
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-[#C6A87C] group-hover:translate-x-0.5 transition-all" />
                  </motion.button>
                ))}
              </div>
            </motion.div>

          </>
        )}
      </AnimatePresence>

      {/* ── MENU MOBILE: popup flutuante posicionado sob o botão ── */}
      <AnimatePresence>
        {menuOpen && (
          <>
            {/* Backdrop para fechar ao clicar fora — mobile */}
            <div
              className="md:hidden fixed inset-0 z-40"
              onClick={() => setMenuOpen(false)}
            />

            {/* Popup flutuante — mobile */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: -8 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="md:hidden fixed top-20 left-6 z-50 w-72 bg-[#111111] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="p-2 space-y-1">
                {menuItems.map((item, index) => (
                  <motion.button
                    key={item.label}
                    initial={{ x: -12, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.06 }}
                    onClick={item.onClick}
                    className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-white/5 border border-transparent hover:border-[#C6A87C]/20 transition-all group rounded-xl"
                  >
                    <div className="w-8 h-8 bg-[#C6A87C]/10 rounded-lg flex items-center justify-center group-hover:bg-[#C6A87C]/20 transition-colors shrink-0">
                      <item.icon className="w-4 h-4 text-[#C6A87C]" strokeWidth={2} />
                    </div>
                    <span className="font-oswald text-sm uppercase tracking-widest text-white group-hover:text-[#C6A87C] transition-colors flex-1 text-left">
                      {item.label}
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-[#C6A87C] group-hover:translate-x-0.5 transition-all" />
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Content Container */}
      <div>
        {/* Hero Section com Imagem de Capa */}
        <section id="hero-section" className="relative min-h-[80vh] flex flex-col items-center justify-center overflow-hidden">
          {/* Imagem de fundo com opacidade baixa */}
          <div className="absolute inset-0 z-0">
            <img
              src="/capabrandao.webp"
              alt="Brandão Barbearia"
              width="1920"
              height="1080"
              fetchpriority="high"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#09090b]/50 via-transparent to-[#09090b]" />
          </div>

          {/* Conteúdo centralizado */}
          <div className="relative z-10 flex flex-col items-center text-center px-4 md:px-8 max-w-4xl mx-auto">
            {/* Logo */}
            <div className="mb-19 md:mb-12">
              <img
                src="/brandão.png"
                alt="Brandão Barbearia Logo"
                width="384"
                height="200"
                className="w-64 md:w-80 lg:w-96 h-auto"
              />
            </div>

            {/* Texto e informações */}
            <div className="space-y-6">
              <span className="font-oswald text-xs uppercase tracking-[0.3em] text-[#C6A87C] block">
                Barbearia Premium
              </span>
              <h1 className="font-oswald font-bold text-4xl md:text-6xl lg:text-7xl uppercase tracking-tight leading-none text-white">
                SEU JEITO, SEU ESTILO,<br />
                SUA <span className="text-[#C6A87C]">BARBEARIA</span>
              </h1>
            </div>

            <button
              data-testid="hero-cta-btn"
              onClick={() => navigate('/agendar')}
              className="cursor-pointer font-bold transition-all duration-200 px-5 py-2.5 rounded-full bg-[#B5966D] border border-transparent flex items-center justify-center text-[15px] text-black uppercase font-oswald tracking-wider hover:bg-[#A58860] active:scale-95 mt-8 group mx-auto"
            >
              <span>Agendar Agora</span>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 74 74" height="34" width="34" className="w-[34px] ml-2.5 transition-transform duration-300 ease-in-out group-hover:translate-x-[5px]">
                <circle strokeWidth="3" stroke="black" r="35.5" cy="37" cx="37" />
                <path fill="black" d="M25 35.5C24.1716 35.5 23.5 36.1716 23.5 37C23.5 37.8284 24.1716 38.5 25 38.5V35.5ZM49.0607 38.0607C49.6464 37.4749 49.6464 36.5251 49.0607 35.9393L39.5147 26.3934C38.9289 25.8076 37.9792 25.8076 37.3934 26.3934C36.8076 26.9792 36.8076 27.9289 37.3934 28.5147L45.8787 37L37.3934 45.4853C36.8076 46.0711 36.8076 47.0208 37.3934 47.6066C37.9792 48.1924 38.9289 48.1924 39.5147 47.6066L49.0607 38.0607ZM25 38.5L48 38.5V35.5L25 35.5V38.5Z" />
              </svg>
            </button>

            {/* Botão Meus Agendamentos */}
            <button
              onClick={() => navigate('/meus-agendamentos')}
              className="cursor-pointer font-semibold transition-all duration-200 px-4 py-2 rounded-full bg-transparent border border-[#C6A87C]/50 flex items-center justify-center text-[13px] text-[#C6A87C] uppercase font-oswald tracking-wide hover:bg-[#C6A87C]/10 hover:border-[#C6A87C] active:scale-95 mt-4 group mx-auto"
            >
              <CalendarCheck className="w-4 h-4 mr-2 text-[#C6A87C] " />
              <span>Meus Agendamentos</span>
            </button>
          </div>

          {/* Botão Admin no canto superior direito */}

        </section>

        {/* Monthly Plans Section */}
        <section id="plans-section" className="py-16 md:py-24 relative bg-gradient-to-b from-[#09090b] to-[#0a0a0a]">
          <div className="max-w-5xl mx-auto px-4">
            <div className="mb-12 text-center">
              <span className="font-oswald text-xs uppercase tracking-[0.3em] text-[#C6A87C] block mb-3">
                Economize com nossos
              </span>
              <h2 className="font-oswald font-bold text-3xl md:text-4xl uppercase tracking-wider text-white">
                PLANOS <span className="text-[#C6A87C]">MENSAIS</span>
              </h2>
              <div className="h-1 w-24 bg-gradient-to-r from-[#C6A87C] to-[#B5966D] rounded-full mt-4 mx-auto" />

              {/* Seletor de Unidade */}
              {units.length > 0 && (
                <div id="unit-selector" className="mt-8">
                  <p className="font-oswald text-sm uppercase tracking-widest text-slate-400 mb-4">
                    Selecione a unidade para ver os preços
                  </p>
                  <div className="flex flex-row justify-center gap-3 flex-wrap">
                    {units.map((unit) => {
                      const isSelected = selectedPlanUnitId === unit.id;
                      return (
                        <button
                          key={unit.id}
                          onClick={() => setSelectedPlanUnitId(isSelected ? null : unit.id)}
                          className={`font-oswald text-sm uppercase tracking-wider px-6 py-3 rounded-lg border-2 transition-all duration-200 inline-flex items-center gap-2 whitespace-nowrap ${
                            isSelected
                              ? 'bg-[#C6A87C] text-black border-[#C6A87C] font-bold'
                              : 'bg-transparent text-[#C6A87C] border-[#C6A87C]/50 hover:border-[#C6A87C] hover:bg-[#C6A87C]/10'
                          }`}
                        >
                          <MapPin className={`w-4 h-4 ${isSelected ? 'text-black' : 'text-[#C6A87C]'}`} />
                          {unit.name}
                        </button>
                      );
                    })}
                  </div>
                  {selectedPlanUnitId && (
                    <p className="font-oswald text-xs text-slate-500 mt-3 uppercase tracking-wider">
                      Mostrando preços para: <span className="text-[#C6A87C]">{units.find(u => u.id === selectedPlanUnitId)?.name}</span>
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {plans.map((plan, index) => {
                const isCurrentPlan = String(userSubscription?.planId) === String(plan.id);
                const isPopular = plan.price === Math.max(...plans.map(p => p.price));

                // Preço efetivo: usa preço da unidade selecionada se existir
                const unitPrice = selectedPlanUnitId
                  ? plan.unit_prices?.find(up => String(up.unit_id) === String(selectedPlanUnitId))?.price
                  : null;
                const displayPrice = unitPrice != null ? unitPrice : plan.price;

                const handleClick = () => {
                  // Obrigar escolha de unidade antes de prosseguir
                  if (unitNotSelected) {
                    toast.error('Selecione uma unidade antes de assinar o plano.');
                    document.getElementById('unit-selector')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    return;
                  }
                  const selectedUnitName = selectedPlanUnitId
                    ? units.find(u => u.id === selectedPlanUnitId)?.name
                    : null;
                  const planWithUnit = { ...plan, selectedUnitId: selectedPlanUnitId, selectedUnitName, effectivePrice: displayPrice };
                  if (isLoggedIn && isCurrentPlan) {
                    handleRenewPlan(plan.id, planWithUnit);
                  } else if (isLoggedIn && userSubscription) {
                    handleChangePlan(plan.id, planWithUnit);
                  } else {
                    handleSubscribePlan(planWithUnit);
                  }
                };

                const buttonLabel = isLoggedIn && isCurrentPlan
                  ? 'Renovar Plano'
                  : isLoggedIn && userSubscription
                    ? 'Trocar Plano'
                    : 'Assinar Plano';

                // Botão fica visualmente "desabilitado" enquanto nenhuma unidade é selecionada
                // (exceto para quem já está logado renovando/trocando — não precisa de unidade)
                const unitRequired = !isLoggedIn || (!isCurrentPlan && !userSubscription);
                const unitNotSelected = unitRequired && !selectedPlanUnitId;

                return (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="relative group"
                  >
                    <div className={`bg-[#121212] rounded-xl p-6 transition-all duration-300 hover:shadow-lg hover:shadow-[#C6A87C]/20 relative overflow-hidden border-2 ${isPopular
                      ? 'border-[#C6A87C] hover:border-[#B5966D]'
                      : 'border-[#C6A87C]/30 hover:border-[#C6A87C]'
                      }`}>
                      {/* Logo decorativa com opacidade baixa */}
                      <img
                        src="/icon-brandao.png"
                        alt=""
                        aria-hidden="true"
                        width="160"
                        height="160"
                        className="absolute -top-3 -left-4 w-40 h-40 object-contain opacity-[0.06] pointer-events-none select-none"
                      />
                      {/* Badge Popular */}
                      {isPopular && (
                        <div className="absolute top-4 right-4 bg-[#C6A87C] text-black px-3 py-1 rounded-full">
                          <span className="font-oswald text-xs uppercase tracking-wider font-bold">Popular</span>
                        </div>
                      )}

                      <div className="text-center mb-6">
                        <h3 className="font-oswald font-bold text-2xl md:text-3xl uppercase tracking-wide text-white mb-2">
                          {plan.name}
                        </h3>
                        <p className="font-oswald text-[#C6A87C] text-lg uppercase tracking-wider">
                          {plan.description}
                        </p>
                      </div>

                      <div className="mb-6 py-4 border-y border-white/10">
                        <div className="text-center">
                          <span className="font-oswald text-sm text-slate-400 uppercase tracking-wider">Por apenas</span>
                          <div className="flex items-center justify-center gap-1 mt-1">
                            <span className="font-oswald font-bold text-4xl md:text-5xl text-[#C6A87C]">
                              R${displayPrice % 1 === 0 ? displayPrice : displayPrice.toFixed(2)}
                            </span>
                          </div>
                          {unitPrice != null && unitPrice !== plan.price && (
                            <p className="font-oswald text-xs text-slate-500 mt-1 line-through">
                              Preço padrão: R${plan.price % 1 === 0 ? plan.price : plan.price.toFixed(2)}
                            </p>
                          )}
                          {selectedPlanUnitId && unitPrice == null && (
                            <p className="font-oswald text-xs text-[#C6A87C]/60 mt-1 uppercase tracking-wide">
                              Preço padrão
                            </p>
                          )}
                        </div>
                      </div>

                      <Button
                        onClick={handleClick}
                        className={`w-full rounded-lg uppercase font-oswald tracking-wider font-bold px-6 py-4 h-auto transition-all ${
                          unitNotSelected
                            ? 'bg-white/10 text-white/30 cursor-not-allowed border border-white/10'
                            : 'bg-[#C6A87C] text-black hover:bg-[#B5966D] hover:-translate-y-1'
                        }`}
                      >
                        {buttonLabel}
                        <ChevronRight className="ml-2 w-5 h-5" />
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Modal de Autenticação para Planos */}
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => {
            setShowAuthModal(false);
            setSelectedPlanForAuth(null);
          }}
          selectedPlan={selectedPlanForAuth}
        />



        {/* Location Section */}
        <section id="location-section" className="py-16 md:py-24 relative">
          <div className="max-w-4xl mx-auto px-4">
            <div className="mb-10 text-center">
              <span className="font-oswald text-xs uppercase tracking-[0.3em] text-[#C6A87C] block mb-3">
                Venha nos visitar
              </span>
              <h2 className="font-oswald font-bold text-3xl md:text-4xl uppercase tracking-wider text-white">
                NOSSAS UNIDADES
              </h2>
              <div className="h-1 w-24 bg-gradient-to-r from-[#C6A87C] to-[#B5966D] rounded-full mt-4 mx-auto" />
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="h-[420px] skeleton rounded-lg" />
                <div className="h-[420px] skeleton rounded-lg" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {units.map((unit) => {
                  const mapsQuery = encodeURIComponent(unit.address);
                  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`;
                  const embedUrl = `https://www.google.com/maps/embed/v1/place?key=AIzaSyD-9tSrke72PouQMnMX-a7eZSW0jkFMBWY&q=${mapsQuery}&language=pt-BR`;
                  // Fallback embed sem API key — usa search embed
                  const embedFallback = `https://maps.google.com/maps?q=${mapsQuery}&output=embed&hl=pt-BR`;

                  return (
                    <div
                      key={unit.id}
                      className="bg-[#121212] border border-white/10 rounded-xl overflow-hidden flex flex-col"
                    >
                      {/* Map embed */}
                      <div className="relative w-full h-[240px] bg-[#0a0a0a]">
                        <iframe
                          src={embedFallback}
                          width="100%"
                          height="100%"
                          style={{ border: 0 }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title={`Localização ${unit.name}`}
                        />
                      </div>

                      {/* Info */}
                      <div className="p-5 flex flex-col gap-3 flex-1">
                        <h3 className="font-oswald font-bold text-xl uppercase tracking-wide text-white">
                          {unit.name}
                        </h3>

                        <div className="flex items-start gap-3 text-sm text-slate-400">
                          <MapPin className="w-4 h-4 text-[#C6A87C] flex-shrink-0 mt-0.5" />
                          <span className="font-manrope">{unit.address}</span>
                        </div>

                        {unit.phone && (
                          <div className="flex items-center gap-3 text-sm text-slate-400">
                            <Phone className="w-4 h-4 text-[#C6A87C] flex-shrink-0" />
                            <span className="font-manrope">{unit.phone}</span>
                          </div>
                        )}

                        <Button
                          onClick={() => handleSelectUnit(unit.address)}
                          className="mt-auto bg-[#C6A87C] text-black hover:bg-[#B5966D] hover:-translate-y-0.5 rounded-lg uppercase font-oswald tracking-wider font-bold px-6 py-3 h-auto w-full transition-all group"
                        >
                          <MapPin className="w-4 h-4" />
                          Ver no Google Maps
                          <ChevronRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>


        {/* Contact Section */}
        <section id="contact-section" className="py-0 pb-8 bg-zinc-950">
          <div className="container mx-auto px-4">
            <div className="mb-8 text-center">
              <p className="font-oswald text-xs uppercase tracking-[0.3em] text-[#C6A87C] block mb-2 ">
                Entre em contato
              </p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white font-oswald ">
                Horário de Funcionamento
              </h2>
              <div className="h-1 w-16 gold-gradient rounded-full mt-1 mx-auto " />
            </div>

            <div className="max-w-3xl mx-auto space-y-6 mt-12">
                {/* Hours */}
                <div className="rounded-2xl p-6 bg-[#0a0a0a] border border-white/10 overflow-hidden">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="w-6 h-6 text-[#A58860]" />
                    <h3 className="text-lg font-bold text-white font-oswald">Horário de Funcionamento</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-zinc-400">Segunda a Sexta</span>
                      <span className="font-medium text-white">08:00 - 20:00</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-zinc-400">Sábado</span>
                      <span className="font-medium text-white">08:00 - 18:00</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-zinc-400">Domingo</span>
                      <span className="font-medium text-red-400">Fechado</span>
                    </div>
                  </div>
                </div>

                {/* WhatsApp CTA */}
                <button
                  type="button"
                  onClick={() => setShowWhatsAppPopup(true)}
                  className="w-full flex items-center justify-between p-5 bg-green-600 hover:bg-green-700 rounded-xl transition-colors group text-left"
                >
                  <div className="flex items-center gap-3">
                    <svg className="w-6 h-6 text-white shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    <div className="text-left">
                      <p className="font-bold text-white font-oswald">WhatsApp</p>
                      <p className="text-green-100 text-sm">Fale conosco agora</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform shrink-0" />
                </button>


              </div>
            </div>
        </section>

        {/* Footer */}
        <footer className="py-8 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              {/* Logo Brandão - Esquerda */}
              <div className="flex items-center">
                <img
                  src="/logoteste2.png"
                  alt="Brandão Barbearia"
                  width="200"
                  height="64"
                  className="h-16 md:h-10 w-auto scale-[100%]"
                />
              </div>

              {/* Copyright - Centro */}
              <p className="font-manrope text-[13px] text-slate-500 text-center">
                © 2026 Brandão Barbearia. Todos os direitos reservados.
              </p>

              {/* Logo AjuCode - Direita */}
              <a
                href="https://www.ajucode.com.br"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-1 hover:opacity-80 transition-opacity"
              >
                <span className="font-manrope text-sm text-slate-500">
                  Desenvolvido por:
                </span>
                <img
                  src="/logoaju.png"
                  alt="AjuCode"
                  width="120"
                  height="40"
                  className="h-10 md:h-7 w-auto"
                />
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default HomePage;
